// Spec v2 §16.3. A dismissable sheet above the member pages on a phone outside the installed app.
// Never blocks the page: it renders on top and "Not now" closes it. Inside the installed app it
// pings the server once per session so the admin's Reach column knows.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Share, Smartphone, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { installGateDecision, isInAppBrowser, isStandalone, platformOf, readDismissals, readInstalled, recordDismissal, writeInstalled } from "@/lib/push";
import { pingStandalone } from "@/lib/services/push";
import * as C from "@/lib/ladder-portal-copy";

let pinged = false; // once per page load is enough

export function InstallGate({ children }: { children: ReactNode }) {
  const { canPrompt, prompt } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);
  const standalone = isStandalone();
  const decision = useMemo(() => installGateDecision({
    platform: platformOf(navigator.userAgent), standalone, inAppBrowser: isInAppBrowser(navigator.userAgent),
    dismissals: readDismissals(), now: Date.now(), hasInstallPrompt: canPrompt, installed: readInstalled(),
  }), [standalone, canPrompt]);

  useEffect(() => { if (standalone && !pinged) { pinged = true; pingStandalone(true).catch(() => undefined); } }, [standalone]);

  const close = () => { recordDismissal(); setDismissed(true); };
  const copyLink = async () => { try { await navigator.clipboard.writeText(window.location.href); toast.success(C.INSTALL_LINK_COPIED); } catch { toast(window.location.href); } };
  const show = !dismissed && decision !== "hidden";

  return (
    <>
      {children}
      {show && (
        <div role="dialog" aria-labelledby="install-title" className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background p-4 shadow-2xl" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <div className="mx-auto max-w-md space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                <div>
                  <h2 id="install-title" className="font-display text-lg font-bold leading-tight tracking-tight">{C.INSTALL_TITLE}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{decision === "unsupported" ? C.INSTALL_OPEN_IN_BROWSER : C.INSTALL_BODY}</p>
                </div>
              </div>
              <button type="button" aria-label={C.INSTALL_NOT_NOW} className="flex h-11 w-11 shrink-0 items-center justify-center text-muted-foreground" onClick={close}><X className="h-5 w-5" /></button>
            </div>
            {decision === "ios-steps" && (
              <ol className="space-y-1.5 text-sm">
                {C.INSTALL_IOS_STEPS.map((s, i) => (
                  <li key={s} className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 font-mono text-xs font-bold text-primary">{i + 1}</span>
                    <span>{i === 0 ? <>{s} <Share className="inline h-4 w-4 align-text-bottom" aria-hidden /></> : s}</span>
                  </li>
                ))}
              </ol>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="min-h-11 flex-1" onClick={close}>{C.INSTALL_NOT_NOW}</Button>
              {decision === "android-install" && <Button className="min-h-11 flex-1" onClick={async () => { const r = await prompt(); if (r !== "dismissed") setDismissed(true); }}>{C.INSTALL_BUTTON}</Button>}
              {decision === "ios-steps" && <Button className="min-h-11 flex-1" onClick={() => { writeInstalled(); setDismissed(true); }}>{C.INSTALL_IOS_DONE}</Button>}
              {decision === "unsupported" && <Button className="min-h-11 flex-1" onClick={copyLink}>{C.INSTALL_COPY_LINK}</Button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
